import { useMutation } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { postImportApplications } from '@/api/Rover/ApplicationForm';
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookFileInput } from '@/components/UI/HookForm/Form/File';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import type { ValidationFunction } from '@/components/UI/HookForm/types';
import { notifyUser } from '@/store/Toast';
import { sanitizeResponseError } from '@/utils';

const getMaritalStatus = (shortStatus: string) => {
  switch (shortStatus) {
    case 'Y':
      return 'Married';
    case 'N':
      return 'Un-Married';
    default:
      return '';
  }
};

const getGender = (genderCode: string) => {
  switch (genderCode) {
    case 'M':
      return 'Male';
    case 'F':
      return 'Female';
    default:
      return '';
  }
};

export const ImportApplications = () => {
  const { applicationFormId } = useParams<string>();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const importApplicationsData = useMutation(
    (payload: { data: { applications: [] }; applicationFormId: string }) => postImportApplications(payload),
    {
      onSuccess: () => {
        navigate('/app/rover/application_forms');
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Applications import request sent successfully.',
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const handleSubmit = async (values: { file: File[] }) => {
    try {
      if (values.file[0]) {
        const fileReader = new FileReader();
        fileReader.readAsText(values.file[0], 'UTF-8');
        fileReader.onload = (e) => {
          const data = JSON.parse(e.target?.result as string)?.data;
          const formattedData = data.map((d: any) => {
            return {
              contactEmail: '',
              contactName: d['Applicant Name'],
              phoneNumber: d.Phone || '111-111-1111',
              files: [],
              formId: applicationFormId,
              applicationData: {
                scope: {
                  electrical: d.Electrical === '1',
                  heatAndAir: d['Heat & Air'] === '1',
                  painting: d.Painting === '1',
                  plumbing: d.Plumbing === '1',
                  roofing: d.Roofing === '1',
                  structural: d.Structual === '1',
                },
                application: {
                  city: d.City,
                  disabled: d.Disabled,
                  doChildrenLive: '',
                  dob: d.DOB,
                  femaleHeadOfHousehold: '',
                  gender: getGender(d.Gender),
                  hasAssets: d.hasAssets,
                  maritalStatus: getMaritalStatus(d.Married),
                  name: d['Applicant Name'],
                  neighborhood: d.Neighborhood,
                  phone: d.Phone || '111-111-1111',
                  receivedAssistance: d.receivedAssistance,
                  requestedInsurance: d.requestedInsurance,
                  state: d.State,
                  streetAddress: d.Street,
                  wasBuiltBefore1978: d.wasBuiltBefore1978,
                  zipCode: d.Zip,
                  race: d.Race ? [d.Race] : [],
                },
                householdIncome: [],
                applicantInfo: {
                  contactEmail: '',
                  fullName: d['Applicant Name'],
                  contactPhone: d.Phone || '111-111-1111',
                },
              },
              status: 'APPROVED',
            };
          });

          if (formattedData.length > 100) {
            dispatch(
              notifyUser({
                showToast: true,
                toastMessage: 'Only 100 applications can be imported per file',
                toastType: 'failed',
              })
            );
          } else {
            importApplicationsData.mutate({
              data: { applications: formattedData },
              applicationFormId: String(applicationFormId),
            });
          }
        };
      }
    } catch (err) {
      console.log(err);
    }
  };
  const validateJson: ValidationFunction = (value: any) => {
    if (value) {
      const file = (value as File[])[0];
      const extension = file.name.split('.').pop();
      if (extension !== 'json') {
        return 'JSON File is required.';
      }
      return undefined;
    }
    return undefined;
  };
  return (
    <div>
      <h3 className='mb-4'>Import Applications</h3>
      <HookFormProvider mode='all' reValidateMode='onChange'>
        <HookForm onSubmit={handleSubmit} className='p-6'>
          <HookFileInput name='file' accept='.json' label='File' validate={validateJson} />

          <div className='flex gap-4 pt-5 justify-end'>
            <Button label='Cancel' btnStyle='neutral' />
            <Button label='Import' btnStyle='primary' type='submit' />
          </div>
        </HookForm>
      </HookFormProvider>
    </div>
  );
};
